import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { Form, Row, Col, Button, Alert, InputGroup } from 'react-bootstrap';
import {
  useExpenseByIdQuery,
  useCreateExpenseItemMutation,
  useDeleteExpenseItemMutation,
  useUpdateExpenseItemMutation,
  RecordType,
  FileInput,
  Expense,
} from '../../../__generated__/graphql';
import { useToasts } from '../../../../../context/Toast';
import { useCurrencyFormatter } from '../../../../../hooks/useCurrencyFormatter';
import FlairIcon from '../../../../../atomic/atoms/FlairIcon';
import {
  toBase64,
  blobToDataURL,
  MAX_FILE_SIZE,
  FILE_ACCEPT_TYPES,
  type ContentDocument,
  isImageFileFromParams,
} from '../../../../../utils/file';
import { getApiToken } from '../../../../../context/AuthContext';
import {
  ExtraFieldsSupportedObject,
  useObjectExtraFields,
} from '../../../components/ExtraFields';
import { collectFormDataExtraFields } from '../../../components/ExtraFields/logic';
import { recordTypeFormKey } from '../../../components/ExtraFields/logic';
import * as yup from 'yup';
import {
  useExpenseFormState,
  useExpenseDropdownOptions,
  useExpenseSubmit,
  useMerchantField,
} from './hooks';
import {
  DynamicExpenseForm,
  SharedFormButtons,
  ExpenseFormWrapper,
} from './components';
import { ExpenseWrapperProps } from './types';
import {
  REIMBURSEMENT_RECORD_TYPE_KEY,
  AUTO_SAVE_DELAY_CLICK_BASED,
  AUTO_SAVE_DELAY_DEFAULT,
  DEBOUNCE_DELAY,
  CLICK_BASED_AUTO_SAVE_FIELDS,
  type SavedExpenseItem,
  type FormStep,
  type ReimbursementFormData,
  calculateVatAmount,
  findOtherRecordType,
  createDefaultExpenseItemInput,
  buildExpenseItemUpdateInput,
  mapExpenseItems,
  mapExpenseToFormData,
} from './reimbursementHelpers';
import { validateExpense } from '../expenseValidation';
import './ExpenseSaveModal.css';

type FormValues = {
  [recordTypeFormKey]: string;
  description: string;
  expenseDate: Date;
  expenseGroup?: string | null;
  project?: string | null;
  category?: string | null;
  merchant?: string | null;
} & Record<string, unknown>;

type Props = ExpenseWrapperProps & {
  onInvoiceUpload: (recordType: RecordType, invoiceData: FileInput) => void;
  recordType?: RecordType;
};

const ReimbursementFormContent: React.FC<Props> = ({
  onSave,
  onInvoiceUpload,
  onCancel,
  expenseGroupId,
  expenseId: expenseIdProp,
  loading = false,
  recordType,
  expenseFormData,
}) => {
  const { t } = useTranslation();
  const { addSuccess, addError } = useToasts();
  const formatCurrency = useCurrencyFormatter();
  const { watch } = useFormContext();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { rawObjectExtraFields: expenseItemExtraFields } = useObjectExtraFields(
    ExtraFieldsSupportedObject.ExpenseItem,
  );
  const { getFormFields } = useObjectExtraFields(
    ExtraFieldsSupportedObject.Expense,
  );

  const [expenseId, setExpenseId] = useState<string | null>(
    expenseIdProp || null,
  );

  useEffect(() => {
    if (expenseIdProp) {
      setExpenseId(expenseIdProp);
      setCurrentStep('details');
    }
  }, [expenseIdProp]);

  const { data: expenseData } = useExpenseByIdQuery({
    variables: { id: expenseId || '' },
    skip: !expenseId,
  });

  const [createExpenseItemMutation, { loading: creatingItem }] =
    useCreateExpenseItemMutation();
  const [deleteExpenseItemMutation] = useDeleteExpenseItemMutation();
  const [updateExpenseItemMutation, { loading: updatingItem }] =
    useUpdateExpenseItemMutation();

  const {
    formData,
    setFormData,
    handleInputChange,
    isUpdatingExpense,
    hasLoadedInitialData,
    setHasLoadedInitialData,
    autoSaveExpense,
    updateExpenseWithMerchant,
    lastSavedDataRef,
  } = useExpenseFormState<ReimbursementFormData>({
    expenseId,
    initialData: {
      description: '',
      expenseDate: new Date(),
      expenseGroupId: expenseGroupId || '',
      categoryId: '',
      merchantId: '',
      projectId: '',
    },
  });

  const lastChangedFieldRef = useRef<string | null>(null);

  const isLoading = loading || creatingItem || isUpdatingExpense;

  const [currentStep, setCurrentStep] = useState<FormStep>(
    expenseIdProp ? 'details' : 'upload',
  );
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedFileUrl, setUploadedFileUrl] = useState<string | null>(null);
  const lastFetchedInvoiceUrlRef = useRef<string | null>(null);
  const [deletingItemIds, setDeletingItemIds] = useState<Set<string>>(
    new Set(),
  );

  const [error, setError] = useState<string | null>(null);

  const isEditMode = !!expenseIdProp;

  const [initializedFromServer, setInitializedFromServer] = useState(false);
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);
  const [customMerchantOptions, setCustomMerchantOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);

  const allFormValuesWatched = watch();
  const allFormValuesRef = useRef(allFormValuesWatched);
  allFormValuesRef.current = allFormValuesWatched;

  const { handleCreateMerchant } = useMerchantField({
    onMerchantChange: (merchantId: string) =>
      handleInputChange('merchantId', merchantId),
    updateExpenseWithMerchant,
    setMerchantOptions: setCustomMerchantOptions,
    merchantOptions: customMerchantOptions,
  });

  const {
    expenseGroupOptions,
    projectOptions,
    categoryOptions,
    merchantOptions,
  } = useExpenseDropdownOptions(customMerchantOptions, expenseFormData);

  const validateExpenseCallback = useCallback(() => {
    const allFormValues = watch();
    const descriptionValue =
      allFormValues.description && allFormValues.description.trim() !== ''
        ? allFormValues.description
        : formData.description && formData.description.trim() !== ''
        ? formData.description
        : '';
    const formDataWithExtraFields = {
      ...formData,
      ...allFormValues,
      description: descriptionValue,
    };
    const hasInvoiceInState = !!uploadedFile || !!uploadedFileUrl;
    const hasInvoiceInData = !!(
      expenseData?.expense?.ContentDocumentLinks &&
      expenseData.expense.ContentDocumentLinks.length > 0
    );
    return validateExpense(formDataWithExtraFields, {
      getFormFields,
      expenseData,
      validateInvoice: true,
      hasUploadedFile: hasInvoiceInState || hasInvoiceInData,
    });
  }, [
    formData,
    expenseData,
    getFormFields,
    watch,
    uploadedFile,
    uploadedFileUrl,
  ]);

  const { submitExpense, isSubmitting } = useExpenseSubmit({
    expenseId,
    onSave,
    successMessage: t(
      'expenses.reimbursementForm.expenseSubmittedSuccessfully',
    ),
    errorMessage: t('expenses.reimbursementForm.expenseSubmitFailed'),
    validateBeforeSubmit: validateExpenseCallback,
  });

  useEffect(() => {
    const loadImageWithAuth = async () => {
      if (expenseData?.expense) {
        const expense = expenseData.expense;
        if (!initializedFromServer) {
          const initialData = mapExpenseToFormData(expense);
          setFormData(initialData);

          const initialExtraFields = collectFormDataExtraFields(
            allFormValuesRef.current,
          );
          const initialExtraFieldsString = JSON.stringify(initialExtraFields);
          lastSavedDataRef.current = {
            formData: initialData,
            extraFieldsString: initialExtraFieldsString,
          };
          setInitializedFromServer(true);
          setHasLoadedInitialData(true);
        }

        if (uploadedFile) {
          return;
        }

        if (
          expense.ContentDocumentLinks &&
          expense.ContentDocumentLinks.length > 0
        ) {
          const firstDoc = expense.ContentDocumentLinks[0];
          const contentDoc = firstDoc.ContentDocument;

          const sfUrl =
            contentDoc?.previewUrl || firstDoc.viewUrl || firstDoc.downloadUrl;

          if (sfUrl && lastFetchedInvoiceUrlRef.current !== sfUrl) {
            lastFetchedInvoiceUrlRef.current = sfUrl;
            setIsLoadingInvoice(true);
            try {
              const apiToken = getApiToken();
              const response = await fetch(sfUrl, {
                credentials: 'include',
                headers: {
                  Authorization: apiToken || '',
                },
              });

              if (response.ok) {
                const blob = await response.blob();
                const dataUrl = await blobToDataURL(blob);
                setUploadedFileUrl(dataUrl);
                setUploadedFile(null);
              } else {
                setUploadedFileUrl(null);
              }
            } catch (error) {
              console.error('Error loading image:', error);
              setUploadedFileUrl(null);
            } finally {
              setIsLoadingInvoice(false);
            }
          }
        } else if (!uploadedFile) {
          setIsLoadingInvoice(false);
        }

        if (currentStep === 'upload') {
          setCurrentStep('details');
        }
      }
    };

    loadImageWithAuth();
  }, [
    expenseData,
    currentStep,
    isEditMode,
    initializedFromServer,
    uploadedFile,
    lastSavedDataRef,
    setFormData,
    setHasLoadedInitialData,
  ]);

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];

    if (file.size > MAX_FILE_SIZE) {
      addError(t('expenses.form.sizeLimitError'));
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setUploadedFile(file);
    const dataUrl = await blobToDataURL(file);
    setUploadedFileUrl(dataUrl);

    try {
      onInvoiceUpload(recordType as RecordType, {
        fileName: file.name,
        fileContentBase64: await toBase64(file),
      });
    } catch (err) {
      console.error('Error uploading file:', err);
      addError(t('expenses.form.fileUploadError'));
      setUploadedFile(null);
      setUploadedFileUrl(null);
    }
  };

  const handleInputChangeWithTracking = useCallback(
    (field: string, value: unknown) => {
      lastChangedFieldRef.current = field;
      handleInputChange(field, value);
    },
    [handleInputChange],
  );

  const handleAddItemClick = async () => {
    if (!expenseId) {
      addError(t('expenses.reimbursementForm.expenseNotCreated'));
      return;
    }

    try {
      const selectedRecordType = findOtherRecordType(
        expenseItemExtraFields?.recordTypes as RecordType[],
      );

      if (!selectedRecordType?.Id) {
        addError(t('expenses.reimbursementForm.noRecordTypeAvailable'));
        return;
      }

      const result = await createExpenseItemMutation({
        variables: {
          input: createDefaultExpenseItemInput(
            expenseId,
            selectedRecordType.Id,
          ),
        },
      });

      if (!result.data?.expenses?.createExpenseItem?.record) {
        const error = result.data?.expenses?.createExpenseItem?.error;
        addError(error || t('expenses.reimbursementForm.itemCreationFailed'));
      }
    } catch (err) {
      addError(t('expenses.reimbursementForm.itemCreationFailed'));
    }
  };

  const handleUpdateExpenseItem = useCallback(
    async (itemId: string, updates: Partial<SavedExpenseItem>) => {
      if (editingTimerRef.current) {
        clearTimeout(editingTimerRef.current);
      }

      editingTimerRef.current = setTimeout(async () => {
        try {
          const result = await updateExpenseItemMutation({
            variables: {
              input: buildExpenseItemUpdateInput(itemId, updates),
            },
          });

          if (!result.data?.expenses?.updateExpenseItem?.record) {
            addError(t('expenses.reimbursementForm.itemUpdateFailed'));
          }
        } catch (err) {
          console.error('Error updating expense item:', err);
          addError(t('expenses.reimbursementForm.itemUpdateFailed'));
        }
      }, DEBOUNCE_DELAY);
    },
    [updateExpenseItemMutation, addError, t],
  );

  const handleDeleteItem = async (itemId: string) => {
    setDeletingItemIds((prev) => new Set(prev).add(itemId));
    try {
      const result = await deleteExpenseItemMutation({
        variables: {
          id: itemId,
        },
      });
      if (!result.data?.expenses?.deleteExpenseItem?.error) {
        addSuccess(t('expenses.reimbursementForm.itemDeletedSuccessfully'));
      } else {
        addError(result.data.expenses.deleteExpenseItem.error);
        setDeletingItemIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(itemId);
          return newSet;
        });
      }
    } catch (err) {
      addError(t('expenses.reimbursementForm.itemDeletionFailed'));
      setDeletingItemIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }
  };

  useEffect(() => {
    if (!hasLoadedInitialData || !expenseId) {
      return;
    }

    const isClickBasedField =
      lastChangedFieldRef.current &&
      CLICK_BASED_AUTO_SAVE_FIELDS.includes(
        lastChangedFieldRef.current as (typeof CLICK_BASED_AUTO_SAVE_FIELDS)[number],
      );
    const delay = isClickBasedField
      ? AUTO_SAVE_DELAY_CLICK_BASED
      : AUTO_SAVE_DELAY_DEFAULT;

    const timerId = setTimeout(async () => {
      await autoSaveExpense(allFormValuesRef.current);
    }, delay);

    return () => {
      clearTimeout(timerId);
    };
  }, [formData, expenseId, hasLoadedInitialData, autoSaveExpense]);

  const handleSubmitAndSend = async () => {
    setError(null);
    await submitExpense();
  };

  const expenseItems = mapExpenseItems(expenseData?.expense?.expenseItems);
  const editingTimerRef = useRef<NodeJS.Timeout | null>(null);

  if (currentStep === 'upload') {
    return (
      <div className="reimbursement-form">
        <div className="text-center py-5">
          <FlairIcon
            icon="document-outline"
            size="3x"
            className="text-primary mb-3"
          />
          <h5 className="mb-3">
            {t('expenses.reimbursementForm.uploadInvoice')}
          </h5>
          <p className="text-muted mb-4">
            {t('expenses.reimbursementForm.uploadInvoiceDescription')}
          </p>

          {
            <input
              ref={fileInputRef}
              type="file"
              accept={FILE_ACCEPT_TYPES}
              onChange={handleFileSelect}
              className="hiddenInput"
              id="file-upload-input"
            />
          }

          <Button
            variant="primary"
            size="lg"
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading}>
            {isLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"></span>
                {t('expenses.reimbursementForm.uploading')}
              </>
            ) : (
              <>
                <FlairIcon icon="file-arrow-up" className="me-2" />
                {t('expenses.reimbursementForm.selectFile')}
              </>
            )}
          </Button>

          <p className="text-muted small mt-3">
            {t('expenses.reimbursementForm.maxFileSize', { size: '5MB' })}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="reimbursementForm">
      <div>
        {error && (
          <Alert variant="danger" className="mb-4">
            {error}
          </Alert>
        )}

        <Row>
          <Col md={6}>
            <div className="text-center">
              {isLoadingInvoice ? (
                <div className="spinnerContainer">
                  <div
                    className="spinner-border text-primary loadingSpinner"
                    role="status">
                    <span className="visually-hidden">
                      {t('expenses.createEditModal.loading')}...
                    </span>
                  </div>
                  <p className="text-muted mt-3">
                    {t('expenses.reimbursementForm.loadingInvoice')}
                  </p>
                </div>
              ) : uploadedFileUrl ? (
                (() => {
                  const contentDoc: ContentDocument | undefined =
                    expenseData?.expense?.ContentDocumentLinks?.[0]
                      ?.ContentDocument;
                  const isImage = isImageFileFromParams({
                    file: uploadedFile,
                    fileUrl: uploadedFileUrl,
                    contentDoc,
                  });
                  const fileName =
                    uploadedFile?.name || contentDoc?.Title || 'Document';

                  return isImage ? (
                    <img
                      src={uploadedFileUrl}
                      alt="Receipt"
                      className="rounded receiptImage"
                    />
                  ) : (
                    <div className="emptyStateContainer">
                      <FlairIcon
                        icon="document-outline"
                        size="4x"
                        className="text-muted mb-3"
                      />
                      <p className="text-muted mb-2">
                        {t('expenses.reimbursementForm.receiptProcessed')}
                      </p>
                      <p className="small text-muted">{fileName}</p>
                      {uploadedFileUrl && (
                        <a
                          href={uploadedFileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-outline-primary btn-sm mt-2"
                          download={fileName}>
                          <FlairIcon
                            icon="eye-outline"
                            className="me-1"
                            size="sm"
                          />
                          {t('expenses.reimbursementForm.viewDocument')}
                        </a>
                      )}
                    </div>
                  );
                })()
              ) : (
                <div className="emptyStateContainer">
                  <FlairIcon
                    icon="document-outline"
                    size="4x"
                    className="text-muted mb-3"
                  />
                  <p className="text-muted">
                    {t('expenses.reimbursementForm.noFileUploaded')}
                  </p>
                  <Button
                    variant="primary"
                    size="sm"
                    className="mt-2"
                    onClick={() => fileInputRef.current?.click()}>
                    {t('expenses.reimbursementForm.uploadFile')}
                  </Button>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept={FILE_ACCEPT_TYPES}
                onChange={handleFileSelect}
                className="hiddenInput"
              />
            </div>
          </Col>

          <Col md={6}>
            <DynamicExpenseForm
              onValueChange={handleInputChangeWithTracking}
              displayFullDateTime={false}
              values={{
                description: formData.description,
                date: formData.expenseDate,
                categoryId: formData.categoryId,
                merchantId: formData.merchantId,
                projectId: formData.projectId,
                expenseGroupId: formData.expenseGroupId,
              }}
              options={{
                categoryOptions,
                projectOptions,
                expenseGroupOptions,
                merchantOptions,
              }}
              onCreateMerchant={handleCreateMerchant}
              categoryRequired={true}
              merchantRequired={true}
            />
          </Col>
        </Row>

        <div className="mb-4 mt-4 me-2 ms-2">
          <div className="mb-2 text-muted small fw-semibold expenseItemsHeader">
            <div>{t('expenses.reimbursementForm.notes')}</div>
            <div>{t('expenses.reimbursementForm.taxRate')}</div>
            <div>{t('expenses.reimbursementForm.taxAmount')}</div>
            <div>{t('expenses.reimbursementForm.amount')}</div>
            <div></div>
          </div>

          {expenseItems.length > 0 ? (
            <div className="itemsContainer">
              {expenseItems.map((item) => {
                const amount =
                  item.flair__Amount__c || item.flair__Manual_Amount__c || 0;
                const vatRate = item.tax?.flair__Percentage__c || 0;
                const vatAmount = calculateVatAmount(amount, vatRate);
                const isDeleting = deletingItemIds.has(item.Id);

                return (
                  <div key={item.Id} className="expenseItemRow">
                    <div>
                      <Form.Control
                        type="text"
                        placeholder={t(
                          'expenses.reimbursementForm.notePlaceholder',
                        )}
                        defaultValue={item.flair__Note__c || ''}
                        onChange={(e) => {
                          handleUpdateExpenseItem(item.Id, {
                            flair__Note__c: e.target.value,
                          });
                        }}
                        disabled={isDeleting}
                      />
                    </div>
                    <div>
                      <Form.Control
                        type="text"
                        value={vatRate > 0 ? `${vatRate.toFixed(2)}%` : ''}
                        placeholder="-"
                        disabled
                        readOnly
                      />
                    </div>
                    <div>
                      <Form.Control
                        type="text"
                        value={vatRate > 0 ? `${vatAmount.toFixed(2)}` : ''}
                        placeholder="-"
                        disabled
                        readOnly
                      />
                    </div>
                    <div>
                      <InputGroup>
                        <Form.Control
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          defaultValue={amount}
                          onChange={(e) => {
                            handleUpdateExpenseItem(item.Id, {
                              flair__Manual_Amount__c: parseFloat(
                                e.target.value,
                              ),
                            });
                          }}
                          disabled={isDeleting}
                        />
                      </InputGroup>
                    </div>
                    <div className="d-flex justify-content-center align-items-center">
                      {isDeleting ? (
                        <div
                          className="spinner-border spinner-border-sm text-danger"
                          role="status">
                          <span className="visually-hidden">
                            {t('expenses.createEditModal.deleting')}...
                          </span>
                        </div>
                      ) : (
                        <Button
                          variant="link"
                          className="text-danger p-0 deleteButton"
                          onClick={() => handleDeleteItem(item.Id)}
                          disabled={isLoading}>
                          <FlairIcon icon="close-outline" size="sm" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="emptyStateMessage">
              {t('expenses.reimbursementForm.noItemsYet')}
            </div>
          )}

          <div className="mt-4 totalRow">
            <div></div>
            <div></div>
            <div></div>
            <div className="d-flex justify-content-between align-items-center">
              <div className="fs-4 fw-bold ms-2">
                {t('expenses.fields.finalAmountLabel')}:
              </div>
              <div
                className={`text-end me-2 fs-3 fw-bold ${
                  isUpdatingExpense ? 'text-primary' : 'text-success'
                }`}>
                {expenseData?.expense?.flair__Final_Amount__c !== undefined &&
                expenseData?.expense?.CurrencyIsoCode
                  ? formatCurrency({
                      value: expenseData.expense.flair__Final_Amount__c,
                      currency: expenseData.expense.CurrencyIsoCode,
                    })
                  : '?'}
              </div>
            </div>
            <div className="d-flex justify-content-center">
              <Button
                variant="outline-secondary"
                className="d-flex align-items-center justify-content-center addItemButton"
                onClick={handleAddItemClick}
                disabled={isLoading || updatingItem}
                title={t('expenses.reimbursementForm.addItem')}>
                <FlairIcon icon="add-outline" size="sm" />
              </Button>
            </div>
          </div>
        </div>

        <SharedFormButtons
          onCancel={onCancel}
          onSubmit={handleSubmitAndSend}
          isLoading={isLoading || updatingItem}
          isSubmitting={isSubmitting}
          isUpdating={isUpdatingExpense || updatingItem}
          submitDisabled={!!formData.expenseGroupId}
          submitDisabledTooltip={t(
            'expenses.perDiemForm.submitDisabledTooltip',
          )}
          showGroupWarning={!!formData.expenseGroupId}
        />
      </div>
    </div>
  );
};

export const ReimbursementForm: React.FC<Props> = (props) => {
  const getDefaultValues = (
    expense: Expense | null,
    expenseGroupId?: string,
  ): FormValues => {
    return {
      [recordTypeFormKey]: REIMBURSEMENT_RECORD_TYPE_KEY,
      description: expense?.flair__Description__c || '',
      expenseDate: expense?.flair__Date_From__c
        ? new Date(expense.flair__Date_From__c)
        : new Date(),
      expenseGroup: expense?.flair__Expense_Group__c || expenseGroupId || null,
      project: expense?.project?.Id || null,
      category: expense?.expenseCategory?.Id || null,
      merchant: expense?.merchant?.Id || null,
    };
  };

  const getValidationSchema = () => {
    return yup.object().shape({});
  };

  return (
    <ExpenseFormWrapper<FormValues>
      wrapperProps={props}
      getDefaultValues={getDefaultValues}
      getValidationSchema={getValidationSchema}>
      <ReimbursementFormContent {...props} />
    </ExpenseFormWrapper>
  );
};
